import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, List, Brain, Activity, LogOut, User, Settings, Users, Wallet, UsersRound, BarChart3, BookOpen, Inbox, Menu, X, ShieldBan
} from 'lucide-react';

const allNavItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { to: '/jobs', icon: List, label: 'Jobs' },
  { to: '/inbox', icon: Inbox, label: 'Email Inbox', roles: ['admin'] },
  { to: '/email-blacklist', icon: ShieldBan, label: 'Blacklist', roles: ['admin'] },
  { to: '/knowledge', icon: Brain, label: 'Knowledge Base', roles: ['admin'] },
  { to: '/analytics', icon: BarChart3, label: 'Analytics', roles: ['admin'] },
  { to: '/billing', icon: Wallet, label: 'Billing' },
  { to: '/team', icon: UsersRound, label: 'Team', roles: ['customer', 'admin'] },
  { to: '/users', icon: Users, label: 'Users', roles: ['admin'] },
  { to: '/api-docs', icon: BookOpen, label: 'API Docs', roles: ['admin'] },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export function AppShell() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const filteredItems = allNavItems.filter(item => !item.roles || item.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="h-1 header-gradient" />

      {/* Top bar */}
      <header className="border-b border-border px-4 md:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Activity className="w-5 h-5 text-primary flex-shrink-0" />
          <h1 className="text-base md:text-lg font-semibold tracking-tight truncate" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            KI Delisting Agent
          </h1>
          <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded font-mono hidden sm:inline">
            v1.0
          </span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {filteredItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) => cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              )}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
          <div className="ml-4 pl-4 border-l border-border flex items-center gap-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="w-4 h-4" />
              <span data-testid="current-user">{user?.name || user?.email || 'User'}</span>
              {user?.role && (
                <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded font-mono uppercase">{user.role}</span>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn" className="text-muted-foreground hover:text-foreground" title="Logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </nav>

        {/* Mobile hamburger */}
        <Button variant="ghost" size="sm" className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <div className="lg:hidden border-b border-border bg-background/95 backdrop-blur-sm px-4 py-3 space-y-1" data-testid="mobile-menu">
          {filteredItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) => cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors w-full',
                isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              )}
              data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
          <div className="pt-2 mt-2 border-t border-border flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="w-4 h-4" />
              <span>{user?.name || user?.email}</span>
              {user?.role && <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded font-mono uppercase">{user.role}</span>}
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 px-4 md:px-6 py-4 md:py-6">
        <Outlet />
      </main>
    </div>
  );
}
